/**
 * Sparkline — a tiny dependency-free SVG line graph for the diagnostic hub.
 * Reusable primitive (the hub had only text/lists before this). Pass a rolling
 * number[] (oldest→newest); it autoscales and renders a polyline + a readout of
 * the latest value. No deps, no per-frame cost beyond the parent's re-render.
 */
import React from 'react';

export interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  label?: string;
  /** Format the latest value for the readout (e.g. bytes→"3.2K/s"). */
  format?: (v: number) => string;
}

export const Sparkline: React.FC<SparklineProps> = ({
  data, width = 180, height = 38, color = '#4fc3f7', label, format,
}) => {
  const n = data.length;
  const max = Math.max(1, ...data);
  const min = Math.min(0, ...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = n <= 1 ? 0 : (i / (n - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const latest = n ? data[n - 1] : 0;
  const readout = format ? format(latest) : String(Math.round(latest));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <svg
        width={width}
        height={height}
        preserveAspectRatio="none"
        style={{ background: 'rgba(0,0,0,0.28)', borderRadius: 4, flex: '0 0 auto' }}
      >
        {n > 1 && <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} />}
      </svg>
      <div style={{ fontSize: 11, fontFamily: 'monospace', lineHeight: 1.3 }}>
        {label && <div style={{ opacity: 0.65 }}>{label}</div>}
        <div style={{ color, fontWeight: 600 }}>{readout}</div>
      </div>
    </div>
  );
};
